import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { LegalAction, MatchPublicView } from '../../api-client.js';
import { computeBallPosition } from '../../lib/afl/ball-position.js';
import { cellToZone, viewerZoneName, zoneToXY, type FieldLane, type FieldLine } from '../../lib/afl/field-coords.js';
import { HandCard, type HandCardData } from '../HandCard.js';
import { DisposalLayer } from './DisposalLayer.js';
import { Field } from './Field.js';
import { FootballToken } from './FootballToken.js';
import { OnFieldPlayers } from './OnFieldPlayers.js';
import { TimeOfDayDial } from './TimeOfDayDial.js';

// Re-declared locally so this component does not depend on the page
// importing it. Keep in sync with MatchPage.tsx until the types move
// into a shared module.
type HandCard = { id?: string; kind?: string; name?: string; hidden?: boolean };

type AflContestSide = {
  seatId: string;
  cards: Array<{ id: string; name?: string }>;
  statTotal: number;
  /** Position-tier bonuses, evaluated in this side's own frame. */
  positionTotal?: number;
  diceTotal: number;
  total: number;
};

type AflTeamView = {
  score: { goals: number; behinds: number };
  total: number;
  handCount: number;
  deckCount: number;
  discardCount: number;
  hand: HandCard[];
};

export type AflRuckView = {
  rounds: number;
  nominated: Record<string, boolean>;
  yourNominationCardId: string | null;
  lastReveal: {
    cards: Record<string, string>;
    outcome: 'player_vs_nonplayer' | 'both_player_decided' | 'both_player_tie' | 'both_nonplayer';
    winnerSeatId: string | null;
  } | null;
};

/** A rover committed to the clearance auction, with its STANDING at the spot. */
export type RoverView = HandCardData & { cardId?: string; inPosition?: boolean; tier?: string | null };

export type AflClearanceView = {
  ruckWinnerSeatId: string;
  ruckLoserSeatId: string;
  activeSeatId: string;
  ruckman: { name: string; power: number };
  /** Open auction — committed rovers per seat as the ACTUAL CARD (full data to
   *  render) plus their STANDING at the stoppage (in position? tier?). Decided
   *  position → tier → power. */
  commits: Record<string, RoverView[]>;
};

/** "t2" / "T2" → 2 (99 = untiered, sorts last). */
export const roverTierNumber = (t?: string | null): number => (t ? Number(String(t).slice(1)) : 99);

/** Clearance standing: in-position first, then tier, then power. Lower = better. */
function roverKey(r: RoverView): [number, number, number] {
  return [r.inPosition ? 0 : 1, r.inPosition ? roverTierNumber(r.tier) : 99, -(r.stats?.power ?? 0)];
}

export function cmpRovers(a: RoverView, b: RoverView): number {
  const ka = roverKey(a),
    kb = roverKey(b);
  for (let i = 0; i < 3; i++) if (ka[i] !== kb[i]) return ka[i] - kb[i];
  return 0;
}

/** The rover currently winning a side's clearance auction (null = none committed). */
export function bestRover(rovers: RoverView[]): RoverView | null {
  return rovers.length ? rovers.reduce((m, r) => (cmpRovers(r, m) < 0 ? r : m)) : null;
}

export type AflViewerState = {
  quarter: number;
  phase: string;
  possessionSeatId: string | null;
  possessionsThisQuarter: number;
  maxPossessionsPerQuarter: number;
  lastEventSummary: string;
  winnerSeatId: string | null;
  completedReason: string | null;
  /**
   * Match-long environmental conditions; null on legacy matches that
   * pre-date the environment slice.
   */
  environment?: {
    // Conditions modify a real STAT (skill), never an action — see
    // docs/tcg-card-conventions.md / docs/sports-tcg-rules.md.
    weather: { id: string; name: string; scale: number } | null;
    wind: { direction: number; scale: number } | null;
    stadium: { id: string; name: string; rating: number; indoor: boolean; modifiesStat: string; bonus: number } | null;
    time: { id: string; name: string; index: number; cost: number } | null;
  } | null;
  /**
   * The universal Conditions slot — a replaceable, symmetric, leaguewide
   * override (a base-stat SET or a position-rule SUPPRESSION) applied to all
   * players on both sides. Null until a Conditions card is played.
   */
  conditions?: {
    cardId: string;
    name: string;
    ownerSeatId: string;
    spec:
      | { type: 'stat_set'; stat: string; value: number }
      | { type: 'position_suppress'; mode: 'tier' | 'position' };
  } | null;
  contest: {
    kind: string;
    nextEscalatorSeatId: string | null;
    attackerSide: AflContestSide;
    defenderSide: AflContestSide;
    maxCardsPerSide: number;
  } | null;
  ruck: AflRuckView | null;
  clearance: AflClearanceView | null;
  teams: Record<string, AflTeamView>;
  /** Authoritative ball position on the 15-zone field. */
  ball?: { line: 'd50' | 'hb' | 'centre' | 'hf' | 'f50'; lane: 'flank_a' | 'corridor' | 'flank_b' } | null;
  /** Frame anchor for the ball cell while possession is null (spot stoppages). */
  ballFrameSeatId?: string | null;
  /** Player cards standing on the ground, in their zones (cleared at stoppage). */
  onField?: OnFieldCardView[];
  /** Card id of the current ball-carrier (one of `onField`). */
  carrierCardId?: string | null;
  /** Set while a kick awaits the marking forward / the opponent's response. */
  pendingKick?: {
    destLine: 'd50' | 'hb' | 'centre' | 'hf' | 'f50';
    destLane?: 'flank_a' | 'corridor' | 'flank_b';
    destName?: string;
    kickerSeatId: string;
    /** The forward committed to lead the mark (null during kick_target). */
    targetCardId?: string | null;
    targetName?: string | null;
  } | null;
  /**
   * The most recent ruck reveal — BOTH nominated cards with full card data,
   * persisted past the ruck so the bluff payoff stays visible in open play.
   */
  lastRuckReveal?: {
    outcome: NonNullable<AflRuckView['lastReveal']>['outcome'];
    winnerSeatId: string | null;
    round: number;
    cards: Record<string, HandCard & Record<string, unknown>>;
  } | null;
  /**
   * The most recent RESOLVED contest of ANY kind (ruck / mark / clearance /
   * kick-mark) — each side's cards, the winner and the WHY in numbers —
   * persisted so it can be reviewed after the play moves on. Feeds the
   * "Last contest" panel (the unified successor to the ruck-reveal box).
   */
  lastContest?: {
    kind: 'ruck' | 'mark' | 'clearance' | 'kick_mark';
    winnerSeatId: string | null;
    summary: string;
    round: number | null;
    sides: Array<{ seatId: string; detail: string; cards: Array<HandCard & Record<string, unknown>> }>;
  } | null;
  /** Set while a run awaits the defender's tackle window. */
  pendingRun?: {
    runnerSeatId: string;
    runnerName?: string;
    runnerTier?: number;
    runnerSpeed?: number;
    destLine: 'd50' | 'hb' | 'centre' | 'hf' | 'f50';
    destLane?: 'flank_a' | 'corridor' | 'flank_b';
    destName?: string;
  } | null;
};

export type OnFieldCardView = {
  cardId: string;
  seatId: string;
  line: 'd50' | 'hb' | 'centre' | 'hf' | 'f50';
  lane: 'flank_a' | 'corridor' | 'flank_b';
  role: 'carrier' | 'support' | 'beaten';
  isCarrier: boolean;
  card: {
    name?: string;
    kind?: string;
    position?: string;
    stats?: Record<string, number>;
  } | null;
};

/**
 * Map the authoritative ball (line + lane, from the possessor's
 * perspective) to a position on the field drawn from the viewer's
 * perspective (their goal on the right). Returns null when the engine
 * hasn't supplied a ball (legacy states) so the caller can fall back.
 */
function authoritativeBall(
  view: AflViewerState,
  rightSeatId: string,
): { x: number; y: number; zone?: 'left_50' | 'right_50' | 'centre' } | null {
  const ball = view.ball;
  if (!ball) return null;
  // The possessor attacks toward their goal; the viewer's goal is on the
  // right, so the possessor attacks right iff they are the right seat.
  // During a spot stoppage (possession null) the cell stays anchored to
  // ballFrameSeatId — without it the ball would render on the wrong side
  // for one of the viewers.
  const frameSeatId = view.possessionSeatId ?? view.ballFrameSeatId ?? null;
  const attackingRight = frameSeatId == null || frameSeatId === rightSeatId;
  const { x, y } = zoneToXY(ball.line, ball.lane, attackingRight);
  // Only highlight a field region when the ball genuinely sits in it: the
  // 50m arcs (f50/d50) or the centre square. At half-forward/half-back the
  // ball token alone marks the spot — don't light up the centre by default.
  let zone: 'left_50' | 'right_50' | 'centre' | undefined;
  if (ball.line === 'f50') zone = attackingRight ? 'right_50' : 'left_50';
  else if (ball.line === 'd50') zone = attackingRight ? 'left_50' : 'right_50';
  else if (ball.line === 'centre') zone = 'centre';
  return { x, y, zone };
}

export type AflFieldViewProps = {
  view: AflViewerState;
  match: MatchPublicView;
  userSeatId: string | null;
  /**
   * Click-to-move disposal. When the viewer has picked a player to dispose
   * with, the field lights up that player's legal destinations; tapping one
   * submits the matching action. Omitted (or empty) → no targets shown.
   */
  disposal?: {
    actions: LegalAction[];
    busy: boolean;
    onChoose: (action: LegalAction) => void;
  };
  /**
   * Fill the parent box (height + width) instead of the default
   * aspect-locked mat. Used by the viewport-fit match layout where the
   * field is sized by its flex cell.
   */
  fill?: boolean;
  /** Tap handler for the on-field player cards (preview / use). */
  onPlayerClick?: (card: OnFieldCardView) => void;
  /**
   * Tap a printed field cell to inspect the position: which cards each coach
   * has standing there. Receives the cell's viewer-facing name and the engine
   * zone (possessor frame) so the caller can filter the on-field set.
   */
  onInspectPosition?: (info: { name: string; line: FieldLine; lane: FieldLane }) => void;
};

/**
 * Just the field + ball overlay. Page-level layout (sidebars, hand,
 * scoreboard) is owned by MatchPage so the AFL view stays composable.
 *
 * The container caps height at the viewport so the oval never spills
 * below the fold, and uses `mx-auto` to centre when its aspect-ratio
 * makes it narrower than its column.
 */
export function AflFieldView({ view, match, userSeatId, disposal, fill, onPlayerClick, onInspectPosition }: AflFieldViewProps) {
  // Right/left assignment: the viewer's seat is always on the right
  // (your goal). For spectators (no userSeatId), fall back to the seat
  // ordering on the match so the field stays deterministic.
  const orderedSeats = [...match.seats].sort((a, b) => a.orderIndex - b.orderIndex);
  const seat0 = orderedSeats[0]?.id ?? '';
  const seat1 = orderedSeats[1]?.id ?? '';

  const rightSeatId = userSeatId ?? seat0;
  const leftSeatId = rightSeatId === seat0 ? seat1 : seat0;

  const seatLabel = (seatId: string) =>
    match.seats.find((s) => s.id === seatId)?.seatKey ?? seatId.slice(0, 6);

  // Prefer the engine's authoritative ball; fall back to the legacy
  // heuristic for states that pre-date the 15-zone model.
  const ball = authoritativeBall(view, rightSeatId) ?? computeBallPosition(view, rightSeatId, leftSeatId);

  const possessionLabel =
    view.possessionSeatId === null ? null : seatLabel(view.possessionSeatId);
  const isYoursInPossession =
    view.possessionSeatId !== null && view.possessionSeatId === userSeatId;

  // On-field players share the ball's frame: all coords are in the
  // possessor's attacking perspective (or the stoppage frame anchor while
  // possession is null), so use the same attackingRight everywhere.
  const frameSeatId = view.possessionSeatId ?? view.ballFrameSeatId ?? null;
  const attackingRight = frameSeatId == null || frameSeatId === rightSeatId;
  // For a participant, "yours" = your seat; for a spectator, treat the
  // right-hand seat as the indigo side so the colours stay legible.
  const isYouSeat = (seatId: string) =>
    userSeatId ? seatId === userSeatId : seatId === rightSeatId;

  const onFieldPlayers = view.onField ?? [];
  const hasCarrier = onFieldPlayers.some((c) => c.isCarrier);

  // While a kick is in flight (no carrier), show the ball at the contest
  // DESTINATION rather than the source — that's where the mark happens, and
  // it keeps the footy off the kicker standing at the source.
  const pk = view.pendingKick;
  const ballPos =
    pk && pk.destLine
      ? zoneToXY(pk.destLine, pk.destLane ?? 'corridor', attackingRight)
      : { x: ball.x, y: ball.y };

  // The cell the ball is in, named in the viewer's frame so it reads off
  // the same labels printed on the ground (flank vs pocket vs corridor).
  const ballZoneName =
    pk && pk.destLine
      ? viewerZoneName(pk.destLine, pk.destLane ?? 'corridor', attackingRight)
      : view.ball
        ? viewerZoneName(view.ball.line, view.ball.lane, attackingRight)
        : null;

  const fieldNode = (
    <Field
        leftLabel={seatLabel(leftSeatId)}
        rightLabel={seatLabel(rightSeatId)}
        leftIsYou={leftSeatId === userSeatId}
        rightIsYou={rightSeatId === userSeatId}
        ballX={ballPos.x}
        ballY={ballPos.y}
        highlight={ball.zone}
        onCellClick={
          onInspectPosition
            ? (cell) => {
                const { line, lane } = cellToZone(cell.col, cell.row, attackingRight);
                onInspectPosition({ name: viewerZoneName(line, lane, attackingRight), line, lane });
              }
            : undefined
        }
        overlay={
          onFieldPlayers.length > 0 ? (
            <OnFieldPlayers
              onField={onFieldPlayers}
              attackingRight={attackingRight}
              isYouSeat={isYouSeat}
              onCardClick={onPlayerClick}
            />
          ) : undefined
        }
        targets={
          disposal && disposal.actions.length > 0 ? (
            <DisposalLayer
              actions={disposal.actions}
              ball={view.ball ?? null}
              attackingRight={attackingRight}
              busy={disposal.busy}
              onChoose={disposal.onChoose}
            />
          ) : undefined
        }
        ballOverlay={
          // When a real player is carrying the ball, the carrier card (with
          // its football badge) IS the possession indicator — don't also
          // float the abstract footy token on top of it. Show the token only
          // when nobody is holding the ball (ruck / kick in flight).
          hasCarrier ? undefined : (
            <FootballToken
              possessionLabel={possessionLabel}
              isYours={isYoursInPossession}
              phase={view.phase}
              contestKind={pk ? 'marking_contest' : view.contest?.kind}
              quarter={view.quarter}
              zoneName={ballZoneName}
            />
          )
        }
      />
  );

  // Two sizing modes, both locked to the field's TRUE 17:10 aspect so the
  // oval (and its labels) never stretch:
  //   fill   → measure the parent box and letterbox the biggest 17:10 rect
  //            into it (viewport-fit match layout).
  //   default→ aspect-locked horizontal mat, centred, capped at 960px.
  if (fill) {
    return <AspectFit ratio={17 / 10}>{fieldNode}</AspectFit>;
  }
  return (
    <div className="mx-auto aspect-[17/10] w-full max-w-[960px] lg:max-h-[calc(100vh-7rem)]">
      {fieldNode}
    </div>
  );
}

/**
 * Letterboxes children at a fixed aspect ratio inside whatever box the
 * parent provides — the layout-safe replacement for stretching the field
 * SVG with preserveAspectRatio="none".
 */
function AspectFit({ ratio, children }: { ratio: number; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) {
        // Not laid out yet (parent viewport-fill hasn't applied its height —
        // child effects run first). Fall back to filling the box; the
        // observer / rAF below corrects us once real dimensions exist.
        setBox(null);
        return;
      }
      let w = r.width;
      let h = w / ratio;
      if (h > r.height) {
        h = r.height;
        w = h * ratio;
      }
      setBox({ w: Math.floor(w), h: Math.floor(h) });
    };
    measure();
    // Re-measure on the next frame (parent layout effects have run by then)
    // and on window resize as a belt for observer-throttled contexts.
    const raf = requestAnimationFrame(measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ratio]);

  return (
    <div ref={ref} className="flex h-full w-full items-center justify-center">
      <div style={box ? { width: box.w, height: box.h } : undefined} className={box ? '' : 'h-full w-full'}>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scoreboard + contest panels — moved from MatchPage. Behaviour is
// preserved verbatim aside from the new layout context.
// ---------------------------------------------------------------------------

/**
 * Shared chrome for the detail-column panels: dark body under a coloured
 * gradient header strip (the panel-scale echo of the card header strips).
 */
export function Panel({
  tone,
  title,
  right,
  children,
}: {
  tone: 'slate' | 'amber' | 'emerald' | 'indigo';
  title: ReactNode;
  right?: ReactNode;
  children: ReactNode;
}) {
  // Premium surface (foil top-rule, dark leather grain, raised) with a
  // coloured, foil-underlined header band. Tones map to the heritage
  // language — oxblood for contests, pine for clearances, steel for the
  // scoreboard, panel for commentary.
  const header = {
    slate: 'bg-gradient-to-r from-ec-panel-2 to-ec-panel',
    amber: 'bg-gradient-to-r from-ec-oxblood-deep to-ec-oxblood',
    emerald: 'bg-gradient-to-r from-[#284530] to-ec-defence',
    indigo: 'bg-gradient-to-r from-[#42535f] to-ec-ruck',
  }[tone];
  return (
    <section className="ec-surface overflow-hidden">
      <div className={`ec-surface__head ${header}`}>
        <h3 className="ec-surface__title">{title}</h3>
        {right}
      </div>
      <div className="p-3">{children}</div>
    </section>
  );
}

const PHASE_LABELS: Record<string, string> = {
  ruck_nomination: 'Ruck — nominate a card',
  clearance: 'Clearance — rove the tap',
  attacker_move: 'Attacker to play',
  defender_move: 'Defender to respond',
  kick_smother: 'Kick — smother window',
  kick_target: 'Kick — choose who leads at the mark',
  kick_response: 'Kick — respond to the mark',
  run_tackle: 'Run — tackle window',
  stadium_auction: 'Stadium — venue auction',
  kickin_pocket: 'Kick-in — choose the pocket',
  contest_escalation: 'Contest — escalate or decline',
  between_quarters: 'Between quarters',
  ended: 'Match ended',
};

export function humanizePhase(phase: string): string {
  return PHASE_LABELS[phase] ?? phase.replace(/_/g, ' ');
}

/** "skill" → "Skill" — conditions name the STAT they modify (never an action). */
function capStat(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function AflScoreBoard({
  view,
  match,
  userSeatId,
}: {
  view: AflViewerState;
  match: MatchPublicView;
  userSeatId: string | null;
}) {
  const seatIds = Object.keys(view.teams);
  const env = view.environment;
  return (
    <Panel
      tone="indigo"
      title="Scoreboard"
      right={
        <span className="truncate text-[10px] font-semibold text-white/85">
          Q{view.quarter} · {view.possessionsThisQuarter}/{view.maxPossessionsPerQuarter} ·{' '}
          {humanizePhase(view.phase)}
        </span>
      }
    >
      {((env && (env.weather || env.wind || env.stadium)) || view.conditions) && (
        <div className="mb-3 flex flex-wrap gap-1.5 text-[11px]">
          {view.conditions && (() => {
            const c = view.conditions!;
            const desc =
              c.spec.type === 'stat_set'
                ? `Set ${capStat(c.spec.stat)} = ${c.spec.value} · all players`
                : c.spec.mode === 'tier'
                  ? 'Tiers disregarded — in/out of position only'
                  : 'Positions off — contests on stat alone';
            return (
              <span
                className="rounded-full border border-ec-pos-event/70 bg-[rgba(123,104,176,0.16)] px-2 py-0.5 font-display uppercase tracking-wide text-ec-bone"
                title={`Conditions — a leaguewide ruling for ALL players, both sides: ${desc}. One universal slot, replaceable: a new Conditions card discards it.`}
              >
                § {c.name} · {desc}
              </span>
            );
          })()}
          {env?.stadium && (
            <span
              className="rounded-full border border-ec-brass/50 bg-[rgba(245,168,28,0.10)] px-2 py-0.5 font-display uppercase tracking-wide text-ec-brass"
              title={
                env.stadium.bonus !== 0
                  ? `Home ground: ${env.stadium.bonus > 0 ? '+' : ''}${env.stadium.bonus} ${env.stadium.modifiesStat} for the home side`
                  : 'Standard venue — no stat bonus'
              }
            >
              ⚑ {env.stadium.name}
              {env.stadium.indoor ? ' · indoor' : ''}
              {env.stadium.bonus !== 0
                ? ` · ${capStat(env.stadium.modifiesStat)} ${env.stadium.bonus > 0 ? '+' : ''}${env.stadium.bonus}`
                : ''}
            </span>
          )}
          {env?.weather && (() => {
            const indoor = !!env.stadium?.indoor;
            const skill = indoor ? 0 : -Math.max(0, env.weather.scale - 1);
            return (
              <span
                className="rounded-full border border-ec-ruck/60 bg-[rgba(62,68,77,0.12)] px-2 py-0.5 font-display uppercase tracking-wide text-ec-bone"
                title={indoor ? 'Indoor venue — weather has no effect' : `Weather scale ${env.weather.scale} of 6 — Skill ${skill} to every kick & shot`}
              >
                ☁ {env.weather.name}
                {indoor ? ' · no effect (indoor)' : skill !== 0 ? ` · Skill ${skill}` : ' · no effect'}
              </span>
            );
          })()}
          {env?.wind && (() => {
            const indoor = !!env.stadium?.indoor;
            const mag = Math.max(0, env.wind.scale - 1);
            const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
            const dir = COMPASS[((env.wind.direction % 8) + 8) % 8] ?? '?';
            return (
              <span
                className="rounded-full border border-ec-defence/60 bg-[rgba(85,167,47,0.12)] px-2 py-0.5 font-display uppercase tracking-wide text-ec-bone"
                title={indoor ? 'Indoor venue — no wind' : mag === 0 ? 'Calm — no wind effect' : `Wind ${dir}: Skill +${mag} kicking with it, −${mag} into it`}
              >
                💨 Wind {dir}
                {indoor ? ' · no effect (indoor)' : mag === 0 ? ' · calm' : ` · Skill ±${mag}`}
              </span>
            );
          })()}
          {env?.time && (() => {
            const indoor = !!env.stadium?.indoor;
            const skill = indoor ? 0 : -env.time.index;
            return (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-ec-brass/50 bg-[rgba(245,168,28,0.12)] px-2 py-0.5 font-display uppercase tracking-wide text-ec-bone"
                title={indoor ? 'Indoor venue — lit, no time-of-day penalty' : skill !== 0 ? `${env.time.name} — Skill ${skill} on every shot (failing light)` : `${env.time.name} — full light, no penalty`}
              >
                <TimeOfDayDial index={env.time.index} indoor={indoor} name={env.time.name} />
                {env.time.name}
                {indoor ? ' · no effect (indoor)' : skill !== 0 ? ` · Skill ${skill}` : ''}
              </span>
            );
          })()}
        </div>
      )}
      {/*
        Always two columns — even on phones — so the score line is a
        single horizontal glance rather than a long vertical scroll.
      */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {seatIds.map((seatId) => {
          const team = view.teams[seatId]!;
          const seat = match.seats.find((s) => s.id === seatId);
          const isYou = seatId === userSeatId;
          const hasPossession = seatId === view.possessionSeatId;
          return (
            <div
              key={seatId}
              className={`rounded-lg border p-2 ${
                isYou
                  ? 'border-ec-ruck/70 bg-[rgba(62,68,77,0.16)] shadow-[inset_0_1px_0_rgba(62,68,77,0.3)]'
                  : 'border-ec-line bg-ec-ground'
              }`}
            >
              <p className="truncate font-display text-xs font-semibold uppercase tracking-wide text-ec-face">
                {seat?.seatKey ?? seatId.slice(0, 8)}
                {isYou && ' (you)'}
                {hasPossession && (
                  <span
                    className="ml-1 animate-pulse text-ec-brass motion-reduce:animate-none"
                    title="In possession"
                  >
                    ●
                  </span>
                )}
              </p>
              <p className="flex items-baseline gap-1.5">
                {/* Keyed on the score so a change remounts it and pops. */}
                <span
                  key={team.total}
                  className="inline-block animate-score-pop font-display text-2xl font-bold tabular-nums text-ec-face motion-reduce:animate-none sm:text-3xl"
                >
                  {team.total}
                </span>
                <span className="font-display text-xs font-medium tabular-nums text-ec-bone-dim">
                  {team.score.goals}.{team.score.behinds}
                </span>
                <span
                  className="ml-auto text-[10px] text-ec-bone-dim"
                  title={`Hand ${team.handCount} · Deck ${team.deckCount}`}
                >
                  ✋{team.handCount} 📚{team.deckCount}
                </span>
              </p>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

export function AflContestPanel({
  contest,
  userSeatId,
}: {
  contest: NonNullable<AflViewerState['contest']>;
  userSeatId: string | null;
}) {
  const side = (s: AflContestSide) => {
    const yours = s.seatId === userSeatId;
    return (
      <div
        className={`rounded-lg border p-2.5 ${
          yours ? 'border-ec-ruck/70 bg-[rgba(62,68,77,0.16)]' : 'border-ec-oxblood/50 bg-ec-ground'
        }`}
      >
        <p className="font-display text-[11px] font-semibold uppercase tracking-wide text-ec-face">
          {yours ? 'You' : 'Opponent'} · {s.cards.length}/{contest.maxCardsPerSide}
        </p>
        <p className="mt-1">
          <span
            key={s.total}
            className="inline-block animate-score-pop font-display text-2xl font-bold tabular-nums text-ec-face motion-reduce:animate-none"
          >
            {s.total}
          </span>{' '}
          <span className="text-[11px] tabular-nums text-ec-bone-dim">
            stat {s.statTotal}
            {(s.positionTotal ?? 0) > 0 && (
              <span className="text-ec-go"> + pos {s.positionTotal}</span>
            )}{' '}
            + 🎲 {s.diceTotal}
          </span>
        </p>
        <ul className="mt-1.5 space-y-0.5 text-xs text-ec-bone">
          {s.cards.map((c, i) => (
            <li key={i} className="truncate">
              · {c.name ?? c.id}
            </li>
          ))}
        </ul>
      </div>
    );
  };
  return (
    <Panel
      tone="amber"
      title="⚔ Current contest · marking"
      right={
        <span className="truncate text-[10px] font-semibold text-white/85">
          {contest.nextEscalatorSeatId === userSeatId ? 'Your decision' : 'Waiting on opponent'}
        </span>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        {side(contest.attackerSide)}
        {side(contest.defenderSide)}
      </div>
      {contest.nextEscalatorSeatId === userSeatId && (
        <p className="mt-2 text-[11px] text-ec-bone">
          Commit another player from your hand, or decline and let the totals stand.
        </p>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Ruck + clearance panel — new stoppage mechanic
// ---------------------------------------------------------------------------

export const RUCK_REVEAL_LABEL: Record<NonNullable<AflRuckView['lastReveal']>['outcome'], string> = {
  player_vs_nonplayer: 'Player vs non-player — the player wins outright.',
  both_player_decided: 'Both nominated players — higher power wins.',
  both_player_tie: 'Both players tied on power — go again.',
  both_nonplayer: 'Neither side nominated a player — both draw and go again.',
};

export function AflRuckPanel({
  ruck,
  match,
  userSeatId,
}: {
  ruck: AflRuckView;
  match: MatchPublicView;
  userSeatId: string | null;
}) {
  const seatLabel = (seatId: string) => {
    if (seatId === userSeatId) return 'You';
    return match.seats.find((s) => s.id === seatId)?.seatKey ?? 'Opponent';
  };
  return (
    <Panel
      tone="amber"
      title={`🏉 Current contest · ruck (round ${ruck.rounds})`}
      right={
        <span className="truncate text-[10px] font-semibold text-white/85">
          {ruck.yourNominationCardId ? 'Waiting for opponent' : 'Nominate a card'}
        </span>
      }
    >
      <ul className="mb-2 space-y-1 text-xs">
        {Object.entries(ruck.nominated).map(([seatId, ready]) => (
          <li key={seatId} className="flex items-center gap-1.5">
            <span className={ready ? 'text-ec-go' : 'animate-pulse text-ec-brass motion-reduce:animate-none'}>
              {ready ? '✔' : '…'}
            </span>
            <span className="text-ec-bone">
              {seatLabel(seatId)} {ready ? 'nominated' : 'choosing'}
            </span>
          </li>
        ))}
      </ul>
      {/* The previous round's reveal now lives in the persistent "Last contest"
          box, so it isn't duplicated here. */}
      <p
        className="mt-1 truncate text-[11px] text-ec-bone-dim"
        title="Player card beats any non-player. Two players → higher power wins. Tie or neither-a-player → discard both and nominate again."
      >
        Player beats non-player · power decides · tie = go again
      </p>
    </Panel>
  );
}

export function AflClearancePanel({
  clearance,
  match,
  userSeatId,
  onPreview,
}: {
  clearance: AflClearanceView;
  match: MatchPublicView;
  userSeatId: string | null;
  onPreview?: (card: HandCardData) => void;
}) {
  const seatLabel = (seatId: string) => {
    if (seatId === userSeatId) return 'You';
    return match.seats.find((s) => s.id === seatId)?.seatKey ?? 'Opponent';
  };
  const youAreUp = clearance.activeSeatId === userSeatId;
  // The single best rover wins by STANDING — position → tier → power (NOT the
  // count, NOT raw power). The comparison is shared with the hand-strip
  // prompt (bestRover / cmpRovers) so both always agree on who leads.
  const loserBest = bestRover(clearance.commits[clearance.ruckLoserSeatId] ?? []);
  const winnerBest = bestRover(clearance.commits[clearance.ruckWinnerSeatId] ?? []);
  const overallBest = [loserBest, winnerBest]
    .filter((r): r is RoverView => !!r)
    .reduce<RoverView | null>((m, r) => (m && cmpRovers(m, r) <= 0 ? m : r), null);

  const side = (seatId: string, role: 'winner' | 'loser') => {
    const rovers = clearance.commits[seatId] ?? [];
    const best = bestRover(rovers);
    const leads = !!best && best === overallBest;
    const isYou = seatId === userSeatId;
    return (
      <div
        className={`rounded-lg border p-2 ${
          isYou ? 'border-ec-ruck/70 bg-[rgba(62,68,77,0.16)]' : 'border-ec-oxblood/50 bg-ec-ground'
        }`}
      >
        <p className="flex items-center justify-between font-display text-[11px] font-semibold uppercase tracking-wide text-ec-face">
          <span>
            {seatLabel(seatId)} · ruck {role}
            {role === 'winner' && <span className="text-ec-bone-dim"> (acts last)</span>}
          </span>
          {clearance.activeSeatId === seatId && (
            <span className="animate-pulse text-ec-brass motion-reduce:animate-none">▸ to act</span>
          )}
        </p>
        {rovers.length === 0 ? (
          <p className="mt-1 text-xs text-ec-bone-dim">— no rover yet —</p>
        ) : (
          <div className="mt-1 flex flex-wrap items-start gap-1">
            {rovers.map((r, i) => {
              const isBest = !!best && r === best;
              return (
                <div key={r.cardId ?? i} className="flex w-14 origin-top scale-[0.7] flex-col items-center">
                  {/* The ACTUAL card the coach committed (not a description). */}
                  <div className={isBest && leads ? 'rounded-xl ring-2 ring-ec-brass' : best ? 'opacity-70 saturate-[0.85]' : ''}>
                    <HandCard
                      card={r as HandCardData}
                      size="sm"
                      onClick={onPreview ? () => onPreview(r as HandCardData) : undefined}
                    />
                  </div>
                  <span className={`mt-0.5 text-center text-[9px] leading-tight ${r.inPosition ? 'text-ec-go' : 'text-ec-bone-dim'}`}>
                    {r.inPosition ? `in position, tier ${roverTierNumber(r.tier)}` : 'out of position'} · power {r.stats?.power ?? 0}{isBest && leads ? ' ★' : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Panel
      tone="emerald"
      title="Current contest · clearance"
      right={
        <span className="truncate text-[10px] font-semibold text-white/85">
          {youAreUp ? 'Your move' : `Waiting on ${seatLabel(clearance.activeSeatId)}`}
        </span>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        {side(clearance.ruckLoserSeatId, 'loser')}
        {side(clearance.ruckWinnerSeatId, 'winner')}
      </div>
      <p
        className="mt-2 text-[11px] leading-relaxed text-ec-bone-dim"
        title="The ruck loser roves first; the winner responds last. Commit a player or stop. The single best rover wins by power + in-position bonus (an in-position rover beats a higher-power out-of-position one) — not the most players. Equal top total → fresh ruck. If the loser doesn't rove, the ruckman gathers it."
      >
        Best single rover wins by power + position (not most) · loser first, winner last · tie = re-ruck ·
        ruckman ({clearance.ruckman.name}, power {clearance.ruckman.power}) gathers if the loser declines
      </p>
    </Panel>
  );
}
